import type { User } from '@/contexts/AuthContext';

// Helper function to check if user has active subscription access
export const hasActiveSubscriptionAccess = (user: User | null): boolean => {
  if (!user) return false;
  
  // Check for subscription_status = inactive (user started checkout but didn't complete payment)
  if (user.subscription_status === 'inactive') {
    return false;
  }
  
  // Use new backend fields if available
  if (user.isCancelledButActive === true) {
    return user.subscribed === true; // Backend sets this correctly for cancelled but active subs
  }
  
  // Fallback to legacy logic for backwards compatibility
  if (user.subscriptionCanceled && user.subscriptionEnd) {
    const endDate = new Date(user.subscriptionEnd);
    const now = new Date();
    return now <= endDate; // Still has access until end date
  }
  
  // Free and free_premium users always have access
  if (user.subscriptionTier === 'free' || user.subscriptionTier === 'free_premium') {
    return true;
  }
  
  // For paid tiers, must have active or cancelled status (cancelled but still within period)
  if (user.subscriptionTier && ['basic', 'standard', 'premium', 'free_premium'].includes(user.subscriptionTier)) {
    // Must have active subscription status or be cancelled but still active
    return user.subscription_status === 'active' || user.subscription_status === 'cancelled' || user.subscribed === true;
  }
  
  return false;
};

// Helper function to check if user should be redirected to subscription selection
export const shouldRedirectToSubscriptionSelect = (user: User | null): boolean => {
  if (!user) return false;
  
  // Users with inactive subscription status (started checkout but didn't complete payment)
  if (user.subscription_status === 'inactive') {
    return true;
  }
  
  // Free and free_premium users always have access, never redirect
  if (user.subscriptionTier === 'free' || user.subscriptionTier === 'free_premium') {
    return false;
  }
  
  return false;
};

// Helper function to check if subscription is cancelled but still active
export const isSubscriptionCancelledButActive = (user: User | null): boolean => {
  if (!user) return false;
  
  // Check for subscription_status = cancelled from backend
  if (user.subscription_status === 'cancelled') {
    // Check if still within access period using willExpireAt or subscriptionEnd
    const expireDate = user.willExpireAt || user.subscriptionEnd;
    if (expireDate) {
      const endDate = new Date(expireDate);
      const now = new Date();
      return now <= endDate; // Cancelled but still within access period
    }
  }
  
  // Use new backend field if available
  if (user.isCancelledButActive !== undefined) {
    return user.isCancelledButActive === true;
  }
  
  // Fallback to legacy logic for backwards compatibility
  if (!user.subscriptionCanceled) return false;
  
  if (user.subscriptionEnd) {
    const endDate = new Date(user.subscriptionEnd);
    const now = new Date();
    return now <= endDate; // Cancelled but still within access period
  }
  
  return false;
};

// Helper function to check if subscription has fully expired
export const isSubscriptionExpired = (user: User | null): boolean => {
  if (!user) return false;
  
  // Use new backend fields if available
  if (user.willExpireAt && user.subscription_status === 'cancelled') {
    const expireDate = new Date(user.willExpireAt);
    const now = new Date();
    return now > expireDate; // Past the expiration date
  }
  
  // Fallback to legacy logic for backwards compatibility
  if (user.subscriptionCanceled && user.subscriptionEnd) {
    const endDate = new Date(user.subscriptionEnd);
    const now = new Date();
    return now > endDate; // Past the end date
  }
  
  return false;
};

// Helper function to get subscription status text
export const getSubscriptionStatus = (user: User | null): string => {
  if (!user) return 'No subscription';
  
  if (isSubscriptionExpired(user)) {
    return 'Expired';
  }
  
  if (isSubscriptionCancelledButActive(user)) {
    const expireDate = user.willExpireAt || user.subscriptionEnd;
    if (expireDate) {
      const formattedDate = new Date(expireDate).toLocaleDateString();
      return `Cancelled (Active until ${formattedDate})`;
    }
    return 'Cancelled (Active until end date)';
  }
  
  if (user.subscribed || hasActiveSubscriptionAccess(user)) {
    return 'Active';
  }
  
  return 'No subscription';
};